import { API } from 'aws-amplify'
import { Notification, IncomingNotification, NotificationGroup } from '../models/notification'
import {
    getNotifications,
    setNotifications,
    getNotificationTokenSaved,
    setNotificationTokenSaved
} from '../utilities'
import firebase from 'firebase/app'
import 'firebase/messaging'
import { setNotificationCount } from '../features/global/nav/notifications'

const notificationLifetime = Number(process.env.REACT_APP_NOTIFICATION_LIFETIME || 14400) * 1000 // default to 4 hours
let dispatchFunction: any | undefined

const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: '',
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
}

firebase.initializeApp(firebaseConfig)

async function subscribeToNotifications(token: string): Promise<any> {
    await API.post('clickfrenzyFeNotifications', '/subscribe', { body: { token } })
    setNotificationTokenSaved(true)
}

async function registerForNotifications() {
    if (!isMessagingSupported()) {
        return
    }

    const messaging = firebase.messaging()

    if (!getNotificationTokenSaved()) {
        if (!(window as any).Notification) {
            return
        }

        const permission = await (window as any).Notification.requestPermission()

        if (permission !== 'granted') {
            return
        }

        let token = null
        try {
            token = await messaging.getToken()
        } catch (e) {
            console.log(e)
        }
        if (token === null) {
            return
        }

        subscribeToNotifications(token)
    }

    messaging.onTokenRefresh(async () => {
        const token = await messaging.getToken()

        if (token === null) {
            return
        }

        subscribeToNotifications(token)
    })

    messaging.onMessage(handleIncomingNotification)
}

function handleIncomingNotification(incomingNotification: IncomingNotification) {
    const notifications = getNotifications()
    const newNotification: Notification = {
        id: incomingNotification.data.id,
        title: incomingNotification.data.title,
        url: incomingNotification.data.url,
        body: incomingNotification.data.content,
        sent: Number(incomingNotification.data.sendTime),
        isNew: true,
    }

    notifications[newNotification.id] = newNotification

    updateNotifications(notifications)
    expireOldNotifications()
}

function expireOldNotifications() {
    const notifications = getNotifications()
    const now = new Date().getTime()
    const cleanedNotifications = Object.keys(notifications).reduce(
        (prev, current) => {
            if (notifications[current].sent + notificationLifetime < now) {
                return prev
            }

            return {
                ...prev,
                [current]: notifications[current],
            }
        },
        {}
    )

    updateNotifications(cleanedNotifications)
}

function updateNotifications(notifications: NotificationGroup) {
    setNotifications(notifications)

    if (dispatchFunction) {
        dispatchFunction(setNotificationCount(Object.keys(notifications).length))
    }
}

function isMessagingSupported() {
    return firebase.messaging.isSupported()
}

function markNotificationsAsRead() {
    const notifications = getNotifications()

    const updatedNotifications = Object.keys(notifications).reduce(
        (prev, current) => {
            return {
                ...prev,
                [current]: {
                    ...notifications[current],
                    isNew: false,
                },
            }
        },
        {}
    )

    setNotifications(updatedNotifications)
}

// clear out old notifications and periodically check for them,
// when the page loads
function initialiseNotifications(dispatch: any) {
    dispatchFunction = dispatch
    expireOldNotifications()
    window.setInterval(expireOldNotifications, 10000)
}

export {
    subscribeToNotifications,
    registerForNotifications,
    isMessagingSupported,
    markNotificationsAsRead,
    initialiseNotifications,
}
